import { Link } from "react-router-dom";
import { Plus, Search, Settings } from "lucide-react";
import { NotificationsBell } from "@/components/NotificationsBell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";
import { useParams } from "react-router-dom";
import { OrgSwitcher, OrgSwitcherShell, type OrgSummary } from "./OrgSwitcher";
import { ProjectSwitcher } from "./ProjectSwitcher";

interface HeaderProps {
  orgs: OrgSummary[] | undefined;
}

/**
 * GitHub-style global header. 48px tall. Sticky at top.
 * Left: wordmark + breadcrumb (org / project). Each segment is its own
 * dropdown — clicking "org" opens the org switcher, clicking "project"
 * opens the project switcher.
 * Right: search, create+, notifications, theme, avatar.
 */
export function Header({ orgs }: HeaderProps) {
  const { org, project } = useParams<{ org?: string; project?: string }>();
  // Gear target: the org in the URL, else the user's first org.
  const settingsOrg = org ?? orgs?.[0]?.slug;
  return (
    <header className="sticky top-0 z-30 h-12 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="h-full px-3 sm:px-4 flex items-center gap-2 sm:gap-4">
        <Link to="/" className="flex shrink-0 items-center gap-2 group" aria-label="openfront home">
          <span className="text-xl sm:text-2xl leading-none font-display italic text-foreground transition-transform group-hover:-rotate-2">
            openfront
          </span>
        </Link>
        {/* Breadcrumb truncates instead of pushing the action cluster off-screen */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 overflow-hidden">
          <span className="text-subtle-foreground select-none">/</span>
          {orgs ? <OrgSwitcher orgs={orgs} /> : <OrgSwitcherShell />}
          {project && (
            <>
              <span className="text-subtle-foreground select-none">/</span>
              <ProjectSwitcher />
            </>
          )}
        </div>

        <div className="flex-1" />

        <div className="hidden md:flex items-center relative w-64">
          <Search className="absolute left-2.5 h-3.5 w-3.5 text-subtle-foreground pointer-events-none" />
          <Input
            type="search"
            placeholder="Search projects, deployments…"
            className="pl-7 pr-10 h-7 text-sm"
            aria-label="Search"
          />
          <kbd className="absolute right-2 text-2xs font-mono text-subtle-foreground bg-subtle px-1 py-px rounded border border-border-muted">
            ⌘K
          </kbd>
        </div>

        <Button asChild variant="primary" size="sm" className="hidden sm:inline-flex">
          <Link to="/orgs/new/import"><Plus className="h-3.5 w-3.5" /> Import</Link>
        </Button>

        {settingsOrg && (
          <Button asChild variant="ghost" size="icon" aria-label="Organization settings" title="Organization settings">
            <Link to={`/orgs/${settingsOrg}/settings`}><Settings className="h-4 w-4" /></Link>
          </Button>
        )}
        <NotificationsBell />

        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}
